/**
 * QorTrace — Hreflang annotations for SEO (Iter250)
 *
 * Dropped at the top of any public page that should rank in Google's
 * non-English markets. Emits:
 *
 *   <link rel="alternate" hreflang="en" href="https://qortrace.com/<path>">
 *   <link rel="alternate" hreflang="de" href="https://qortrace.com/<path>?lang=de">
 *   ... (one per supported locale)
 *   <link rel="alternate" hreflang="x-default" href="https://qortrace.com/<path>">
 *
 * Google uses these to choose which language version to surface to each
 * searcher, and to deduplicate near-identical pages across locales.
 *
 * Once Phase 11 ships and DNS is on qortrace.com, these go live and
 * Search Console will see them within ~24-72h.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES } from "../i18n";

const PROD_BASE = "https://qortrace.com";

export default function HreflangTags() {
  const location = useLocation();

  useEffect(() => {
    const head = document.head;
    if (!head) return;

    // Remove any previously-injected hreflang tags from prior route
    head.querySelectorAll('link[rel="alternate"][data-i18n-hreflang]').forEach(
      (el) => el.remove()
    );

    // Strip an existing locale prefix from the pathname so we can rebuild
    // it cleanly for every supported language.
    const segments = location.pathname.split("/").filter(Boolean);
    const head0 = (segments[0] || "").toLowerCase();
    const cleanPath = SUPPORTED_LANGUAGE_CODES.includes(head0)
      ? "/" + segments.slice(1).join("/")
      : location.pathname;
    const normalisedPath = cleanPath === "/" ? "" : cleanPath;
    const search = location.search || "";

    const addLink = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-i18n-hreflang", "1");
      head.appendChild(el);
    };

    SUPPORTED_LANGUAGES.forEach(({ code }) => {
      const href = code === "en"
        ? `${PROD_BASE}${normalisedPath || "/"}${search}`
        : `${PROD_BASE}/${code}${normalisedPath}${search}`;
      addLink(code, href);
    });

    // x-default tells Google the canonical fallback (English)
    addLink("x-default", `${PROD_BASE}${normalisedPath || "/"}${search}`);

    return () => {
      head.querySelectorAll('link[rel="alternate"][data-i18n-hreflang]').forEach(
        (el) => el.remove()
      );
    };
  }, [location.pathname, location.search]);

  return null;
}
